import { computed, unref } from 'vue';
import useKillSwitches from '@/composables/useKillSwitches';
import useExperimentIds from '@/composables/useExperimentIds';

/**
 * Universal composable for gating features behind an individual feature killswitch,
 * experiment treatment recipe, and the master killswitch override.
 *
 * Formula:
 * `featureFlag === true && (recipeMatches === true || masterFlag === true)`
 *
 * Developer Usage Guidance:
 * - This composable provides safe holiday code freeze treatment reachability on PLP when
 *   Mercury recipe evaluation is bypassed by upstream systems.
 * - When `plpMasterFlag` (or custom `options.masterFlagKey`) is enabled, recipe matching is
 *   bypassed for any feature whose individual feature killswitch is also enabled.
 * - If the individual feature killswitch is disabled, the feature is immediately inactive
 *   regardless of recipe match or master flag status.
 * - Designed for straightforward post-freeze deprecation/removal (targeted for January 2027).
 *
 * @param {string|boolean|import('vue').Ref<boolean>} featureFlag - Killswitch property key
 *   from useKillSwitches (e.g. 'badgeRedesignEnabled') OR boolean / Ref<boolean>
 * @param {string|string[]} recipeIds - Target experiment treatment recipe ID(s)
 *   (e.g. '3147-21' or ['3158-21', '3158-22'])
 * @param {Object} [options={}] - Optional configuration options
 * @param {string} [options.masterFlagKey='plpMasterFlag'] - Killswitch key for master override
 * @returns {import('vue').ComputedRef<boolean>} Reactive boolean indicating whether the feature treatment is active
 */
export default function useMasterKillSwitch(featureFlag, recipeIds, options = {}) {
  const killSwitches = useKillSwitches();
  const { experimentIds } = useExperimentIds();
  const masterFlagKey = options?.masterFlagKey || 'plpMasterFlag';

  return computed(() => {
    const isFeatureEnabled = typeof featureFlag === 'string' ? Boolean(killSwitches?.[featureFlag]) : Boolean(unref(featureFlag));

    if (!isFeatureEnabled) {
      return false;
    }

    const isMasterActive = Boolean(killSwitches?.[masterFlagKey]);
    if (isMasterActive) {
      return true;
    }

    const ids = Array.isArray(recipeIds) ? recipeIds : [recipeIds];
    return ids.some((id) => experimentIds.value?.includes(id));
  });
}
